import React, { FC, useCallback } from "react";
import PackagesList from "components/PackagesList/PackagesList";
import { List, Map } from "immutable";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { openRightPanel } from "reduxFolder/reducers/rightPanel";
import { getShipment } from "reduxFolder/reducers/storage";
import {
  HeaderTitle,
  InnerSection,
  Section,
  SectionContent,
  SectionHeader,
} from "styles/outgoingStyles";
import { OpenRightPanel } from "types/common/rightPanel";
import { EmptyShipments } from "./ContainerShipments.styles";

interface ContainerShipmentsProps {
  /** A loaded ContainerDetail — its `items` are unwrapped shipments. */
  container: any;
  getShipment: (id: number) => Promise<void>;
  openRightPanel: OpenRightPanel;
}

/** What PackagesList labels the row with, so panel and list always agree. */
const shipmentTitle = (item: any) =>
  item.get("description") ||
  item.get("shipment_tracking_number") ||
  item.get("tracking_number");

const ContainerShipments: FC<ContainerShipmentsProps> = React.memo(
  ({ container, getShipment, openRightPanel }) => {
    const { t } = useTranslation("common");
    const items = container.get("items", List());

    const openShipment = useCallback(
      (item: any) => {
        openRightPanel(
          "STORAGE_ITEM",
          Map({
            readOnly: true,
            id: item.get("id"),
            originalItem: item,
            // StorageItemPanel labels its back button with the consolidation
            // keys and hands them back to returnPreviousPanel — a container
            // plays that role here.
            consolidationId: container.get("id"),
            consolidationSku: container.get("sku"),
            containerId: container.get("id"),
            emblem_thumb_url: item.get("emblem_thumb_url"),
            description: shipmentTitle(item),
            returnPreviousPanel: "CONTAINER_DETAILED",
            // The Storage duck owns the shipment detail cache every opener
            // fills; Containers injects its reducer so this lands there too.
            getShipment,
            // Read-only view: the comment field is rendered disabled, but the
            // panel still calls this on blur.
            updateComment: () => Promise.resolve(),
          }),
        );
      },
      [container, getShipment, openRightPanel],
    );

    return (
      <Section>
        <SectionHeader>
          <HeaderTitle>{`${t("parcels.shipments")} (${items.size})`}</HeaderTitle>
        </SectionHeader>
        <InnerSection>
          <SectionContent>
            {items.size ? (
              <PackagesList extended items={items} handleClick={openShipment} />
            ) : (
              <EmptyShipments>{t("containers.emptyShipments")}</EmptyShipments>
            )}
          </SectionContent>
        </InnerSection>
      </Section>
    );
  },
);

const withConnect = connect(null, { getShipment, openRightPanel });

export default withConnect(ContainerShipments);
